"use client";

import { useEffect } from "react";
import { useJsonResource } from "@/hooks/useJsonResource";
import { useWorkerAttention } from "@/lib/clientWorkerSnapshot";
import { toneBadge } from "@/lib/statusTone";
import ChatThread from "./ChatThread";
import { CloseIcon, ManagerIcon, PanelIcon } from "@/components/icons";

/**
 * Smithers: the global executive assistant that spans all workers. Always-open
 * right rail — ask questions, modify worker configs, or brainstorm automations.
 * `caw:open-chat` focuses the input ("Talk to Smithers" on resource requests).
 */
export default function Manager({
  onHide,
  onCloseDrawer,
  businessId,
  visible,
}: {
  businessId: string | null;
  visible: boolean;
  /** Collapse the desktop column. */
  onHide: () => void;
  /** Dismiss the mobile drawer. */
  onCloseDrawer: () => void;
}) {
  const attention = useWorkerAttention();
  const attentionLabel = `${attention} open resource request${attention === 1 ? "" : "s"}`;
  // Smithers cannot answer at all without a model provider key. Say so up
  // front rather than letting the first message fail with nothing visible.
  // A failed check leaves the banner hidden rather than crying wolf.
  const { data: models, refresh: checkProviders } = useJsonResource<{
    providers?: { available?: boolean }[];
  }>(visible ? "/api/models" : null);
  const needsKey =
    models !== undefined && !(models.providers ?? []).some((p) => p.available);

  useEffect(() => {
    // Keys are added in another tab; look again when this one comes back.
    const onRefresh = () => {
      if (!document.hidden) void checkProviders();
    };
    onRefresh();
    window.addEventListener("caw:activity-refresh", onRefresh);
    document.addEventListener("visibilitychange", onRefresh);
    return () => {
      window.removeEventListener("caw:activity-refresh", onRefresh);
      document.removeEventListener("visibilitychange", onRefresh);
    };
  }, [businessId, checkProviders]);

  useEffect(() => {
    function onOpen() {
      const input = document.getElementById(
        "manager-chat-input"
      ) as HTMLTextAreaElement | null;
      input?.focus();
      input?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    window.addEventListener("caw:open-chat", onOpen);
    return () => window.removeEventListener("caw:open-chat", onOpen);
  }, []);


  return (
    <>
      <header className="flex shrink-0 items-center gap-2.5 border-b border-zinc-800 px-4 py-3 md:h-[var(--page-header-height,4.5rem)]">
        <span className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-violet-600/20 text-violet-300">
          <ManagerIcon className="size-5" />
          {attention > 0 && (
            <span title={attentionLabel} aria-label={attentionLabel}
              className={`absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full border text-[9px] font-bold ring-2 ring-zinc-950 ${toneBadge.attention}`}>
              {attention > 9 ? "9+" : attention}
            </span>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-zinc-100">Smithers</h2>
          <p className="truncate text-[11px] text-zinc-500">
            Ask, adjust, brainstorm across workers
          </p>
        </div>
        <button
          type="button"
          onClick={onHide}
          title="Hide Smithers"
          aria-label="Hide Smithers"
          className="hidden shrink-0 rounded-md p-1.5 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200 md:inline-flex"
        >
          <PanelIcon className="size-4" flipped />
        </button>
        <button
          type="button"
          onClick={onCloseDrawer}
          title="Close Smithers"
          aria-label="Close Smithers"
          className="flex size-8 items-center justify-center rounded-lg text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-100 md:hidden"
        >
          <CloseIcon className="size-5" />
        </button>
      </header>
      {businessId ? <ChatThread
        businessId={businessId}
        visible={visible}
        workerId={null}
        workerName="Smithers"
        inputId="manager-chat-input"
        initialMessages={[]}
        emptyHint="I oversee your workers. Ask what they're up to, have me tweak their configs or schedules, or brainstorm a new automation — I can create the worker for you."
        setupWarning={
          needsKey
            ? "No AI provider API key is configured yet, so Smithers can't reply."
            : null
        }
      /> : <p className="p-4 text-sm text-zinc-400">Select a business to talk to Smithers.</p>}
    </>
  );
}
